import { isHalfDayAbsence } from "@/lib/employeeAbsences";
import type { PlanungAssignment, PlanungAbsence } from "@/lib/montageplanung";
import type { BarGeom } from "./AssignmentBar";
import { assignmentGeom } from "./useAssignmentDrag";
import {
  MP_LAYOUT_DESKTOP,
  type MpLayoutMetrics,
} from "./layoutConstants";
import {
  absenceLayoutKey,
  expandAbsencesForVacationSickness,
} from "./vacationSicknessOverlay";

export interface AssignmentLaneLayout {
  geom: BarGeom;
  lane: number;
}

export interface AbsenceLaneLayout {
  geom: BarGeom;
  lane: number;
  absence: PlanungAbsence;
}

export interface BookedLaneLayout {
  geom: BarGeom;
  lane: number;
}

export interface BookedLayoutSpan {
  key: string;
  dateFrom: string;
  dateTo: string;
}

export interface RowTrackLayout {
  laneCount: number;
  trackHeight: number;
  /** Unterste Spur für den durchgehenden Meros-Zustand. Einsätze liegen darüber. */
  stateLane: number | null;
  assignments: Map<number, AssignmentLaneLayout>;
  absences: Map<string, AbsenceLaneLayout>;
  booked: Map<string, BookedLaneLayout>;
}

interface IntervalItem {
  key: string;
  start: number;
  end: number;
}

/** Halboffenes Intervall [start, end) in Tagesindizes. */
function laneIntervalForGeom(geom: BarGeom): { start: number; end: number } {
  return { start: geom.startIdx, end: geom.startIdx + geom.span };
}

function laneIntervalForAbsence(
  geom: BarGeom,
  absence: Pick<PlanungAbsence, "day_fraction" | "half_day_part">,
): { start: number; end: number } {
  const full = laneIntervalForGeom(geom);
  if (!isHalfDayAbsence(absence) || geom.span !== 1) return full;
  if (absence.half_day_part === "am") {
    return { start: full.start, end: full.start + 0.5 };
  }
  if (absence.half_day_part === "pm") {
    return { start: full.start + 0.5, end: full.end };
  }
  return full;
}

export function absenceBarPosition(
  geom: BarGeom,
  absence: Pick<PlanungAbsence, "day_fraction" | "half_day_part">,
  dayWidth: number,
): { left: number; width: number } {
  const isHalf = isHalfDayAbsence(absence) && geom.span === 1;
  const partOffset = isHalf && absence.half_day_part === "pm" ? 0.5 : 0;
  const partSpan = isHalf ? 0.5 : 1;
  return {
    left: (geom.startIdx + partOffset) * dayWidth + 2,
    width: Math.max(geom.span * dayWidth * partSpan - 4, 8),
  };
}

function assignLanes(items: IntervalItem[]): Map<string, number> {
  const sorted = [...items].sort((a, b) => a.start - b.start || a.end - b.end);
  const laneEnds: number[] = [];
  const lanes = new Map<string, number>();

  for (const item of sorted) {
    let lane = laneEnds.findIndex((end) => end <= item.start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(item.end);
    } else {
      laneEnds[lane] = item.end;
    }
    lanes.set(item.key, lane);
  }

  return lanes;
}

export function computeRowTrackLayout(
  assignments: PlanungAssignment[],
  absences: PlanungAbsence[],
  windowFrom: Date,
  lastIdx: number,
  metrics: MpLayoutMetrics = MP_LAYOUT_DESKTOP,
  options?: { reserveHoursBand?: boolean; booked?: BookedLayoutSpan[]; stateLane?: boolean },
): RowTrackLayout {
  const intervals: IntervalItem[] = [];
  const assignmentGeoms = new Map<number, BarGeom>();
  const absenceGeoms = new Map<string, BarGeom>();
  const bookedGeoms = new Map<string, BarGeom>();
  const boardAbsences = expandAbsencesForVacationSickness(absences);

  for (const a of assignments) {
    const geom = assignmentGeom(a.date_from.slice(0, 10), a.date_to.slice(0, 10), windowFrom, lastIdx);
    if (!geom) continue;
    assignmentGeoms.set(a.id, geom);
    intervals.push({
      key: `a-${a.id}`,
      ...laneIntervalForGeom(geom),
    });
  }

  for (const abs of boardAbsences) {
    const geom = assignmentGeom(abs.date_from.slice(0, 10), abs.date_to.slice(0, 10), windowFrom, lastIdx);
    if (!geom) continue;
    const key = absenceLayoutKey(abs);
    absenceGeoms.set(key, geom);
    intervals.push({
      key: `abs-${key}`,
      ...laneIntervalForAbsence(geom, abs),
    });
  }

  for (const booked of options?.booked ?? []) {
    const geom = assignmentGeom(booked.dateFrom.slice(0, 10), booked.dateTo.slice(0, 10), windowFrom, lastIdx);
    if (!geom) continue;
    bookedGeoms.set(booked.key, geom);
    intervals.push({
      key: `b-${booked.key}`,
      ...laneIntervalForGeom(geom),
    });
  }

  const lanes = assignLanes(intervals);
  let laneCount = Math.max(1, lanes.size > 0 ? Math.max(...lanes.values()) + 1 : 1);
  let stateLane: number | null = null;
  if (options?.stateLane) {
    if (intervals.length > 0) {
      stateLane = laneCount;
      laneCount += 1;
    } else {
      stateLane = 0;
    }
  }

  const assignmentLayouts = new Map<number, AssignmentLaneLayout>();
  for (const [id, geom] of assignmentGeoms) {
    assignmentLayouts.set(id, {
      geom,
      lane: lanes.get(`a-${id}`) ?? 0,
    });
  }

  const absenceLayouts = new Map<string, AbsenceLaneLayout>();
  for (const abs of boardAbsences) {
    const key = absenceLayoutKey(abs);
    const geom = absenceGeoms.get(key);
    if (!geom) continue;
    absenceLayouts.set(key, {
      geom,
      lane: lanes.get(`abs-${key}`) ?? 0,
      absence: abs,
    });
  }

  const bookedLayouts = new Map<string, BookedLaneLayout>();
  for (const [key, geom] of bookedGeoms) {
    bookedLayouts.set(key, {
      geom,
      lane: lanes.get(`b-${key}`) ?? 0,
    });
  }

  const hoursBand = options?.reserveHoursBand ? metrics.hoursBand : 0;
  const contentHeight =
    laneCount * metrics.barH + Math.max(0, laneCount - 1) * metrics.barGap;
  const trackHeight = Math.max(
    metrics.minRowH,
    metrics.rowPadTop + contentHeight + metrics.rowPadBottom + hoursBand,
  );

  return {
    laneCount,
    trackHeight,
    stateLane,
    assignments: assignmentLayouts,
    absences: absenceLayouts,
    booked: bookedLayouts,
  };
}

export function barTopForLane(
  lane: number,
  metrics: MpLayoutMetrics = MP_LAYOUT_DESKTOP,
): number {
  return metrics.rowPadTop + lane * (metrics.barH + metrics.barGap);
}
